// The city's own names on the circle (SPEC-redesign §6.2).
//
// District names used to come from the basemap's `place` labels and the disc's
// rim cut them in half — „GRUNWALD" read „ALD". MapLibre's `within` filter was
// measured against the live tiles and does not keep a point label inside a
// polygon, so the basemap draws no district names at all and these are ours:
// plain marks, positioned with `map.project`, which the rim can be reasoned
// about because we do the reasoning.
//
// WHICH names to draw is this file's rule; the centroids themselves and the name
// a moved centre takes from them live in `state/districts.ts`, where `axis/` and
// `state/` can reach them too (#312).
import { haversine } from '../shared/geo.ts';
import { type District, DISTRICTS } from '../state/districts.ts';

/**
 * Below this the disc is one district's worth of ground, and naming it says
 * only where you already know you are.
 */
const MIN_RADIUS_M = 2000;
/** How far in from the rim a name may sit, so the name itself clears it. */
const INSET = 0.7;
/** More than this and the disc is a list of names with a map behind it. */
const MOST = 5;

/**
 * The districts to draw for one centre and one radius: nearest first, never
 * more than five, never outside 0.7 of the radius, and none at all under 2 km.
 * Pure, so the rule is read and tested without a map.
 */
export function districtsFor(centre: { lat: number; lon: number }, radius: number): District[] {
  if (radius < MIN_RADIUS_M) return [];
  const reach = radius * INSET;
  return DISTRICTS.map((d) => ({ d, away: haversine(centre, d) }))
    .filter(({ away }) => away <= reach)
    .toSorted((a, b) => a.away - b.away || a.d.name.localeCompare(b.d.name, 'pl'))
    .slice(0, MOST)
    .map(({ d }) => d);
}
